<template>
  <form-button
    :label="label"
    :icon="icon"
    :icon-props="iconProps"
    :classes="classes"
    :tooltip="tooltip"
    :disabled="disabled"
    :is-update-in-process="planetStore.isUpdateInProcess"
    :ignore-update-in-process="ignoreUpdateInProcess"
    :variant="variant"
    :data-qa="dataQa"
    :link="link"
    :external-link="externalLink"
    :link-route-name="prefixedLinkRouteName"
    :link-route-params="shellAwareLinkRouteParams"
    :link-query="linkQuery"
    :skip-focus-clear="skipFocusClear"
    :is-loading="isLoading"
    :lazy-tooltip="lazyTooltip"
    @button-click="emit('button-click', $event)"
    @focus="emit('focus', $event)"
    @blur="emit('blur', $event)"
  >
  </form-button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { FormButtonVariant, GenericObject } from '@/types';
import FormButton from 'ui-component-library/rbz/components/FormButton.vue';
import { useBranchesStore } from '@/store/BranchesStore';
import { usePlanetStore } from '@/store/PlanetStore';
import { useContextStore } from '@/store/ContextStore';
import { useRouting } from '@/helpers/RoutingUtils';

// Required label or icon
interface RbzFormButtonProps {
  label?: string;
  icon?: string;
  iconProps?: GenericObject;
  classes?: string;
  tooltip?: string;
  disabled?: boolean;
  ignoreUpdateInProcess?: boolean;
  variant?: FormButtonVariant;
  dataQa?: string;
  link?: string;
  externalLink?: boolean;
  linkRouteName?: string;
  linkQuery?: GenericObject;
  skipFocusClear?: boolean;
  isLoading?: boolean;
  lazyTooltip?: boolean;
}

// This `props` is being used by spec files
 
const props = withDefaults(defineProps<RbzFormButtonProps>(), {
  disabled: false,
  variant: 'primary',
  dataQa: '',
  classes: '',
  tooltip: '',
  lazyTooltip: false,
});

interface RbzFormButtonEmits {
  (e: 'button-click', value: Event): void;

  (e: 'focus', value: Event): void;

  (e: 'blur', value: Event): void;
}

const emit = defineEmits<RbzFormButtonEmits>();

const branchesStore = useBranchesStore();
const planetStore = usePlanetStore();
const contextStore = useContextStore();
const { getRouteName } = useRouting();

const prefixedLinkRouteName = computed(() =>
  props.linkRouteName ? getRouteName(props.linkRouteName) : props.linkRouteName
);

const shellAwareLinkRouteParams = computed(() =>
  contextStore.isShell
    ? undefined
    : { branch: branchesStore.selectedBranchId || branchesStore.defaultBranch }
);
</script>
